import { createSelector } from 'reselect';
import { sortBy, partition, isEmpty } from 'lodash';

const getAllTasks = state => state.taskManagement.tasks;
const getRoomStatus = state => state.taskManagement.roomStatus;
const getRoomTimers = state => state.taskManagement.roomTimers;
const getRoomTimersLoading = state => state.taskManagement.roomTimersLoading;
const getRoom = state => state.roomOptions.room;
const getRoomCompletedTasks = state => state.taskManagement.roomCompletedTasks;
const getDisplayTexts = state => state.statics.displayTexts;

const filterByType = (tasks, roomType) => {
  if (!roomType) {
    return tasks;
  }
  return tasks.filter(t => t.roomType.name === roomType);
};

const filterByTiming = (tasks, roomTiming) => {
  if (!roomTiming) {
    return tasks;
  }
  return tasks.filter(t => t.roomTiming === roomTiming);
};

const arrayToObject = (array, key) => array ?
  array.reduce((obj, item) => {
    obj[item[key]] = item
    return obj
  }, {})
  : {};


//selectors

export const getRoomAvailableTasks = createSelector(
  [getAllTasks, getRoomStatus],
  (allTasks, roomStatus) => {
    const tasks = filterByTiming(filterByType(allTasks, roomStatus.roomType), roomStatus.roomTiming);
    return sortBy(tasks, ['displayOrder']);
  }
);

export const getUpcomingTasks = createSelector(
  [getRoomAvailableTasks, getRoomStatus],
  (roomAvailableTasks, roomStatus) => {
    if (!roomStatus.lastCompletedTaskId) {
      return roomAvailableTasks;
    }
    const completedIdx = roomAvailableTasks.findIndex(t => t.id === roomStatus.lastCompletedTaskId);
    return roomAvailableTasks.slice(completedIdx + 1);
  }
);

export const getCompletedTasks = createSelector(
  [getRoomAvailableTasks, getRoomStatus],
  (roomAvailableTasks, roomStatus) => {
    if (!roomStatus.lastCompletedTaskId) {
      return [];
    }
    const completedIdx = roomAvailableTasks.findIndex(t => t.id === roomStatus.lastCompletedTaskId);
    return roomAvailableTasks.slice(0, completedIdx + 1);
  }
);

export const getCurrentTask = createSelector(
  [getUpcomingTasks],
  upcomingTasks => upcomingTasks[0] || {}
);

export const getMappedTasks = createSelector(
  [getRoomAvailableTasks, getRoomStatus, getRoomCompletedTasks],
  (roomAvailableTasks, roomStatus, roomCompletedTasks) => {
    if (!roomAvailableTasks.length || isEmpty(roomStatus) ||
      (roomStatus && roomStatus.lastCompletedTaskId && !roomCompletedTasks.length) ||
      !roomStatus.roomType) {
      return [];
    }
    if (!roomStatus.lastCompletedTaskId) {
      return roomAvailableTasks;
    }
    const lastCompletedTask = roomAvailableTasks.find(t => t.id === roomStatus.lastCompletedTaskId);
    return roomAvailableTasks.map(task => {
      if (task.displayOrder <= lastCompletedTask.displayOrder) {
        const label = roomCompletedTasks.find(t => t.taskId === task.id) ? 'completed' : 'expired';
        return {
          ...task,
          status: label,
        };
      }
      return {
        ...task,
        status: 'upcoming',
      };
    });
  }
);

//
// export const getTaskGroups = createSelector(
//   [getRoom, getAllTasks],
//   (room, allTasks) => {
//     if (!room || isEmpty(room) || room.loading) {
//       return {
//         completedTasks: [],
//         upcomingTasks: [],
//         expiredTasks: [],
//       };
//     }
//     let tasks = filterByTiming(filterByType(allTasks, room.roomType.name), room.timing);
//     tasks = sortBy(tasks, ['displayOrder']);
//
//     const taskGroups = partition(tasks, t => room.roomTasks.some(rt => rt.taskId === t.id));
//     const completedTasks = taskGroups[0];
//     const lastCompleted = completedTasks[completedTasks.length - 1];
//     const upcomingExpired = lastCompleted ?
//       partition(taskGroups[1], t => t.displayOrder > lastCompleted.displayOrder) :
//       [taskGroups[1], []];
//     return {
//       completedTasks,
//       upcomingTasks: upcomingExpired[0],
//       expiredTasks: upcomingExpired[1],
//     };
//   }
// );

export const getTimersTasks = createSelector(
  [getRoomAvailableTasks, getRoomTimers, getRoomCompletedTasks, getRoomTimersLoading],
  (roomAvailableTasks, roomTimers, roomCompletedTasks, roomTimersLoading) => {
    const timerTasks = [];
    let timerModel = {};

    if (!roomCompletedTasks.length && roomTimersLoading) {
      return timerTasks;
    }

    roomAvailableTasks.forEach((task) => {
      switch (task.timerAction) {
        case 'start': {
          const existentTimer = roomTimers.length ? roomTimers.find(timer => timer.startTask.id === task.id) : {};
          timerModel = {
            ...existentTimer,
            startTask: task,
          };
          return;
        }
        case 'warning': {
          const completedWarning = roomCompletedTasks.find(tsk => tsk.taskId === task.id) || {};
          timerModel = {
            ...timerModel,
            ['warning' + task.timerDurationMinutes]: {
              id: task.id,
              completeDateTime:
                completedWarning ? completedWarning.completeDateTime : completedWarning,
            },
          };
          return;
        }
        case 'stop': {
          timerModel = {
            ...timerModel,
            stopTaskId: task.id,
          };
          timerTasks.push(timerModel);
          timerModel = {};
          return;
        }
        default:
          return;
      }
    });
    return timerTasks;
  }
);

export const getRoomDTO = createSelector(
  [getRoom, (state) => state.tcEvent.roomsDTO, (state) => state.roomOptions.roomDTO],
  (roomAllInfo, roomsDTO, singleRoomDTO) => {
    if (!roomAllInfo?.id) return {};

    if (!isEmpty(roomsDTO)) {
      const found = roomsDTO.find((r) => r.id === roomAllInfo.id);
      if (found) return found;
    }

    if (!isEmpty(singleRoomDTO)) {
      const found = singleRoomDTO.find((r) => r.id === roomAllInfo.id);
      if (found) return found;
    }

    return {};
  }
);

export const mapDisplayTexts = createSelector(
  [getDisplayTexts],
  (displayTexts) => arrayToObject(displayTexts, 'code')
);